"use client";

import Link from "next/link";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface Action {
  id: string;
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
  variant?: "primary" | "secondary" | "success" | "warning";
}

interface QuickActionsProps {
  actions: Action[];
}

const variantStyles = {
  primary: "from-primary-500 to-primary-600 text-white shadow-primary-500/50",
  secondary: "from-secondary-500 to-secondary-600 text-white shadow-secondary-500/50",
  success: "from-success-500 to-success-600 text-white shadow-success-500/50",
  warning: "from-warning-500 to-warning-600 text-white shadow-warning-500/50",
};

export function QuickActions({ actions }: QuickActionsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {actions.map((action) => {
        const Icon = action.icon;
        const variant = action.variant || "primary";
        return (
          <Link
            key={action.id}
            href={action.href as never}
            className="card p-6 group hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 animate-fade-in"
          >
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br shadow-lg group-hover:shadow-xl transition-all duration-300 flex-shrink-0",
                  variantStyles[variant]
                )}
              >
                <Icon className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-bold text-slate-900 group-hover:text-primary-600 transition-colors">
                  {action.label}
                </h4>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                  {action.description}
                </p>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
